import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { ErrorPage } from '@/components/common/ErrorPage';
import { Breadcrumb, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { AuthContext } from '@/contexts';
import { PERMISSION_TYPE, URL_PARAMS } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import CreateEditDeviceForm from '@/pages/Devices/CreateEditDeviceForm';
import { Devices as DeviceIcon } from '@/resources';
import { useCreateDeviceMutation, useGetDeviceQuery, useUpdateDeviceMutation } from '@/services';
import { CreateEditDevicePayload, Organization, SelectOptionProps } from '@/types';

export const CreateEditDevice = () => {
  const { deviceId } = useParams();
  const searchParams = new URLSearchParams(useLocation().search);
  const isEditPage = !!deviceId;
  const [t] = useTranslation('common');
  const [isSubmitting, setIsSubmitting] = useState<boolean>();
  const queryClient = useQueryClient();
  const [backendError, setBackendError] = useState<string>('');
  const navigate = useNavigate();
  const { currentUserData: currentUser, permissions } = useContext(AuthContext);
  const isAdmin = permissions.includes(PERMISSION_TYPE.GLOBAL_ACCESS);
  const [selectedOrganizations, setSelectedOrganizations] = useState<SelectOptionProps[]>([]);
  const label = isEditPage ? t('editDevice') : t('createDevice');
  const [error, setError] = useState<number>();

  useEffect(() => {
    localStorage.removeItem(URL_PARAMS.HID);
  }, []);

  const { mutate: createDevice } = useCreateDeviceMutation(queryClient, {
    onSuccess: () => {
      navigate('/devices');
      showToast(ToastType.SUCCESS, t('deviceCreated'));
      setIsSubmitting(false);
    },
    onError: (error) => {
      if (error.response.data.ErrorMessages[0].Code === 'HardwareIdExists') {
        setBackendError(t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
      } else {
        showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      }
      setIsSubmitting(false);
    }
  });

  const { mutate: updateDevice } = useUpdateDeviceMutation(queryClient, {
    onSuccess: () => {
      navigate(`/devices/${deviceId}`);
      showToast(ToastType.SUCCESS, t('deviceUpdated'));
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  const {
    data: device,
    isLoading: isDeviceLoading,
    isError,
    isRefetching
  } = useGetDeviceQuery(Number(deviceId), {
    onSuccess: () => {
      undefined;
    },
    onError: (error) => {
      setError(error.status);
    }
  });

  useEffect(() => {
    const allowedOrgIds = currentUser?.organizations.map((org) => org.id) ?? [];
    if (isEditPage && device?.data?.organizations?.length > 0) {
      setSelectedOrganizations(
        device?.data.organizations.map((o: Organization) => ({
          value: o.id,
          label: o.name,
          isDisabled: allowedOrgIds.length === 0 ? false : !allowedOrgIds.includes(o.id)
        }))
      );
    } else {
      setSelectedOrganizations([]);
    }
  }, [device]);

  const getOrganizationIds = () => {
    if (device?.data?.organizations) {
      return device.data.organizations.map((o: Organization) => o.id);
    } else if (!isAdmin && !(currentUser?.organizations && currentUser?.organizations.length > 1)) {
      return [currentUser?.organizations[0]?.id];
    } else {
      return [];
    }
  };

  const getInitialValues = () => {
    return {
      id: device?.data.id,
      enabled: !!device?.data.enabled,
      name: device?.data.name || '',
      description: device?.data.description || '',
      hardwareId: device?.data.hardwareId || searchParams.get(URL_PARAMS.HID) || '',
      hardwareInfo: device?.data.hardwareInfo || '',
      organizationIds: getOrganizationIds(),
      playerReleaseId: device?.data.playerRelease?.id || '',
      streamId: device?.data.streamId || ''
    };
  };

  const handleSubmit = (values: CreateEditDevicePayload) => {
    setIsSubmitting(true);
    const payload = {
      id: values.id,
      enabled: isEditPage ? values.enabled : true,
      name: values.name,
      description: values.description,
      hardwareId: values.hardwareId,
      organizationIds:
        isAdmin || (currentUser?.organizations && currentUser?.organizations.length > 1)
          ? values.organizationIds
          : currentUser?.organizations[0]
            ? [currentUser?.organizations[0]?.id]
            : [],
      playerReleaseId: values.playerReleaseId || undefined,
      streamId: values.streamId || undefined
    };
    if (isEditPage) {
      updateDevice(payload);
    } else {
      createDevice(payload);
    }
  };

  return (
    <div className='flex h-full justify-center'>
      {(isEditPage && isDeviceLoading) || isRefetching ? (
        <Spinner />
      ) : error ? (
        <ErrorPage code={error} />
      ) : (
        !isError && (
          <div className='flex w-full flex-col md:mx-20 md:mt-7'>
            <div className='w-full'>
              <Breadcrumb
                icon={<DeviceIcon className='text-primary-blue' />}
                iconPath={'/devices'}
                path={[{ label: label }]}
              />
            </div>
            <div className='mx-3 mt-5 flex flex-col md:mt-9'>
              <Label text={label} type={LABEL_TYPE.H1} />
              <Label
                text={t('generalInformation')}
                type={LABEL_TYPE.H3_BOLD}
                className='mt-5 md:mt-9'
              />
              <CreateEditDeviceForm
                showOrganizationSelect={
                  isAdmin ||
                  (currentUser?.organizations ? currentUser?.organizations.length > 1 : false)
                }
                selectedOrganizations={selectedOrganizations}
                setSelectedOrganizations={setSelectedOrganizations}
                isSubmitting={isSubmitting}
                initialValues={getInitialValues()}
                handleSubmit={handleSubmit}
                isEditPage={isEditPage}
                backendError={backendError}
              />
            </div>
          </div>
        )
      )}
    </div>
  );
};
